/**
 * AdoraCare -- pure view logic for the full-screen PATIENT / TABLET mode
 * (PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1; founder findings F-1/F-2/F-5/F-7/
 * F-9). No IO, no React -- the deterministic derivations the component and the
 * tests share.
 *
 * PATIENT MODE LAW (Section 8): the surface shows ONLY the active intake --
 * no staff nav, no patient/lead/billing lists, no other patient data. It is a
 * bounded confirm-and-correct experience: the patient checks evidence-backed
 * values (each with a human provenance label), confirms or proposes a bounded
 * correction, sees DOB-derived age (never editable when DOB exists), and
 * submits ONCE. A changed identity value becomes PROPOSED CORRECTION EVIDENCE
 * (never a silent overwrite) and the patient lands on a hand-back screen.
 */

import type {
  IntakeAgeView,
  IntakeCorrectionField,
  IntakeCorrectionReason,
  IntakePrefillResult,
  ProposedCorrectionInput,
} from '@/dashboard/api';
import { formatIstDateDdMmYyyy } from '@/lib/datetime-ist';

// --- Copy (exported for the deterministic render tests) ---------------------

export const PATIENT_MODE_TITLE = 'Please confirm your details';
export const PATIENT_MODE_INTRO =
  'We already have these details from your appointment. Please check them and tell us if anything has changed.';
/** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the ONE action names the JOB --
 *  completing the registration -- for both drivers of the one form (the
 *  we-help desk today; the self-fill link later). */
export const PATIENT_MODE_SUBMIT_COPY = 'Complete Registration';
/** F6 (W-5/W-8): the disabled submit carries the remaining count ITSELF --
 *  one signal, never a competing count line beside an enabled-looking
 *  button. */
export function submitLabelForMissing(missingCount: number): string {
  if (missingCount <= 0) return PATIENT_MODE_SUBMIT_COPY;
  return missingCount === 1
    ? 'Complete Registration - 1 answer left'
    : `Complete Registration - ${missingCount} answers left`;
}
/** F5 (W-3): the unlinked (pre-mint) inline-edit note -- the desk lawfully
 *  corrects capture; nothing claims a later staff confirm. */
export const PATIENT_MODE_INLINE_EDIT_NOTE =
  'Updated here - saved with this registration.';
/** F1 (W-9): the pending proposed-changes banner title on resume. */
export const PATIENT_MODE_PENDING_TITLE = 'Changes given earlier - please confirm';
export const PATIENT_MODE_PENDING_PHONE_NOTE =
  'A different phone number was given. Confirm the number with the person and enter it below.';
export const PATIENT_MODE_HANDBACK_TITLE = 'All done - thank you';
export const PATIENT_MODE_HANDBACK_BODY =
  'Please return this device to the receptionist.';
/** The identity-door hand-back (IDENTITY-LINK-AT-INTAKE-V1): the person pick
 *  is a front-desk act -- this surface never shows another patient's
 *  details, only the plain hand-back ask. */
export const PATIENT_MODE_DESK_CONFIRM_COPY =
  'Please hand this device back to the front desk to confirm who this visit is for.';
export const PATIENT_MODE_REVIEW_TITLE = 'Thank you - a staff member will confirm your details';
export const PATIENT_MODE_REVIEW_BODY =
  'You told us some details are different. Please return this device to the receptionist; nothing was changed automatically and they will confirm the details with you.';
export const PATIENT_MODE_PHONE_ON_FILE_COPY = 'Phone number on file';
export const PATIENT_MODE_PHONE_NONE_COPY = 'No phone number on file yet';
export const PATIENT_MODE_PHONE_CONFIRM_COPY = 'This is still my number';
export const PATIENT_MODE_PHONE_CHANGE_COPY = 'Use a different number';
/** FD-30 (the canonical last-4 confirm; UX LAW 1): confirm, never
 *  re-collect -- the title names the ending digits, the why-line says why
 *  we confirm at all. */
export function phoneConfirmTitle(last4: string): string {
  return `Your mobile ends in ${last4} - is it still correct?`;
}
export const PATIENT_MODE_PHONE_WHY_COPY =
  'Confirming your number protects your records and avoids duplicates.';
/** FD-29: the confirmation framing for a known field ("We have: Female -
 *  correct?"). */
export function knownFieldConfirmHint(label: string): string {
  return `We have: ${label} - correct? Tap to change.`;
}
/** FD-40: the intake complaint section vocabulary. */
export const PATIENT_MODE_COMPLAINT_TITLE = 'What brings you in today?';
export const PATIENT_MODE_COMPLAINT_OTHER_PLACEHOLDER =
  'Tell us in your own words (optional)';
/** FD-29: the desk already captured the reason -- confirmed, never
 *  re-asked. */
export function complaintOnFileLine(complaint: string): string {
  return `You told us: ${complaint}`;
}
// (FD-37's intakeStepLine retired with the stage checklist -- F6; no dead
// exports kept, the #246 lesson.)
/** FD-41: the pre-submit summary -- what the doctor receives, composed
 *  honestly from what was actually given. */
export function presubmitSummary(facts: {
  readonly hasComplaint: boolean;
  readonly answerCount: number;
}): string {
  const parts = ['your details'];
  if (facts.hasComplaint) parts.push('your reason for visit');
  if (facts.answerCount > 0) {
    parts.push(
      `your ${facts.answerCount} answer${facts.answerCount === 1 ? '' : 's'}`,
    );
  }
  parts.push('your consent choices');
  return `Your doctor will receive: ${parts.join(', ')}.`;
}
export const PATIENT_MODE_CONFIRM_COPY = 'Correct';
export const PATIENT_MODE_CHANGE_COPY = 'Change';
export const PATIENT_MODE_DOB_UNKNOWN_COPY = 'Date of birth unknown';
export const PATIENT_MODE_HANDBACK_RETURN_COPY =
  'Please return this device to the receptionist.';

// --- Voice (WALK-3-REGISTRATION-CONTAINER-FIX-V1, C2) ------------------------
//
// ONE form-core, TWO thin voice wrappers (founder intent, F3 order). The form
// fields, gates, wire and submit contract are IDENTICAL for both drivers; only
// these labels + the phone affordance differ:
//   - PATIENT voice: the person's own hands (the /patient-intake tablet page,
//     the future WhatsApp self-fill link) -- second-person, confirm-don't-
//     re-collect phone.
//   - DESK voice: the receptionist completing the registration WITH the person
//     inside the day-board modal -- neutral desk labels (never "Your details" /
//     "Your age"), an editable phone field prefilled from the SAME wire.
// The strings are exported so the deterministic render tests pin both voices.

// D5 (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1): `questionnaireIntro` is
// GONE from the voice. The clinical questions no longer render on the
// registration form at all, so a voice line introducing them would introduce
// nothing -- and a dead export is its own defect (the #246 lesson). The
// doctor's Start Consultation block carries its own intro.

/** How the phone cell renders: patient CONFIRM display (full own number +
 *  change door, or the last-4 confirm chip) vs desk EDIT (the known number
 *  prefilled in ONE editable field -- C5). */
export type IntakePhoneMode = 'confirm' | 'edit';

export interface IntakeVoice {
  /** The details-card heading. */
  readonly detailsHeading: string;
  /** The age/DOB section title. */
  readonly ageTitle: string;
  /** The reason-for-visit section title. */
  readonly complaintTitle: string;
  /** The phone affordance shape (see IntakePhoneMode). */
  readonly phoneMode: IntakePhoneMode;
  /** Whether the patient-facing "your doctor will receive" pre-submit
   *  reassurance renders (desk path: OFF -- it is patient copy). */
  readonly showPresubmitSummary: boolean;
  /** How the prefilled identity (name + phone) renders. REGISTRATION-DEPLOYED-
   *  WALK-CLOSURE-V1 (founder visual verdict 2026-08-13): the DESK modal is the
   *  mockup contract, where identity is FIELDS inside the Patient details /
   *  Contact section cards (`fields`); the patient tablet keeps its confirm /
   *  read-back CARD grammar (`card`). */
  readonly identityLayout: 'card' | 'fields';
}

/** The PATIENT / TABLET voice -- byte-identical to the pre-package copy. */
export const PATIENT_INTAKE_VOICE: IntakeVoice = {
  detailsHeading: 'Your details',
  ageTitle: 'Your age',
  complaintTitle: PATIENT_MODE_COMPLAINT_TITLE,
  phoneMode: 'confirm',
  showPresubmitSummary: true,
  identityLayout: 'card',
};

/** The FRONT-DESK voice -- neutral labels, an editable prefilled phone, no
 *  patient-voice reassurance (the staff path carries zero patient copy). */
export const DESK_INTAKE_VOICE: IntakeVoice = {
  detailsHeading: 'Patient details',
  ageTitle: 'Age or date of birth',
  complaintTitle: 'Reason for visit',
  phoneMode: 'edit',
  showPresubmitSummary: false,
  identityLayout: 'fields',
};

/** Reason options offered when the patient changes an identity value. */
export const CORRECTION_REASON_OPTIONS: ReadonlyArray<{
  readonly value: IntakeCorrectionReason
  readonly label: string
}> = [
  { value: 'booking_typo', label: 'There was a typo in the booking' },
  { value: 'name_correction', label: 'My name is spelled differently' },
  { value: 'new_phone_number', label: 'I have a new phone number' },
  { value: 'dob_correction', label: 'My date of birth is different' },
  { value: 'different_person', label: 'This appointment is for a different person' },
  { value: 'external_mr_missing', label: 'I have a hospital / MR number to add' },
  { value: 'other', label: 'Something else' },
]

// --- Age display (F-5: DOB and age can never disagree) ----------------------

/** PATIENT-FACING age text (AUG-12-WALK-BLOCKERS-V1 F8, founder JIT ruling):
 *  a KNOWN age displays; an unknown age returns NULL and the age section
 *  simply asks -- clinic-file vocabulary ("not on file") never renders to
 *  the patient about themselves. */
export function ageDisplayText(age: IntakeAgeView): string | null {
  if (age.ageYears === null) return null;
  if (age.derivedFromDob) return `${age.ageYears} years (from date of birth)`;
  if (age.isApproximate) return `About ${age.ageYears} years (approximate)`;
  return `${age.ageYears} years`;
}

/** F6: the tenant's date grammar on the DOB entry -- dd/mm/yyyy, typed
 *  (the native type="date" control renders the BROWSER locale's order,
 *  mm/dd/yyyy on a US-locale device -- never lawful for this tenant).
 *  Locale-config note: a per-org date-input locale key is a registered
 *  config debt; org 1 is dd/mm by market. */
export const DOB_INPUT_PLACEHOLDER = 'dd/mm/yyyy';

// --- Review rows -------------------------------------------------------------

const FIELD_LABELS: Readonly<Record<string, string>> = {
  full_name: 'Name',
  phone: 'Phone',
  dob: 'Date of birth',
  gender: 'Gender',
  city: 'City / area',
};

export interface PrefillReviewRow {
  readonly field: string;
  readonly label: string;
  readonly displayValue: string | null;
  readonly hasValue: boolean;
  readonly sourceLabel: string;
  readonly identitySensitive: boolean;
  readonly isPhone: boolean;
}

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** FD-33: an ISO date value renders dd/mm/yyyy (Indian locale) through THE
 *  ONE formatter; every other value passes through verbatim. */
function displayValueForField(field: string, value: string | null): string | null {
  if (value === null) return null;
  if (field === 'dob') {
    const formatted = formatIstDateDdMmYyyy(value);
    return formatted !== '' ? formatted : value;
  }
  return value;
}

/** The labeled review rows the patient checks -- values + human provenance. */
export function prefillReviewRows(prefill: IntakePrefillResult): PrefillReviewRow[] {
  return prefill.fields.map((f) => ({
    field: f.field,
    label: fieldLabel(f.field),
    displayValue: displayValueForField(f.field, f.displayValue),
    hasValue: f.hasValue,
    sourceLabel: f.sourceLabel,
    identitySensitive: f.identitySensitive,
    isPhone: f.field === 'phone',
  }));
}

// --- Proposed-correction payload --------------------------------------------

export interface FieldChangeState {
  readonly changed: boolean;
  readonly proposedValue: string;
  readonly reason: IntakeCorrectionReason | '';
}

export const EMPTY_FIELD_CHANGE: FieldChangeState = {
  changed: false,
  proposedValue: '',
  reason: '',
};

export const CORRECTABLE_FIELDS: readonly IntakeCorrectionField[] = [
  'full_name',
  'phone',
  'dob',
  'external_mr',
];

/** Build the proposedCorrections payload from the patient's change state. Only
 *  fields the patient marked changed AND gave a reason for are included. */
export function buildProposedCorrections(
  changes: Partial<Record<IntakeCorrectionField, FieldChangeState>>,
): ProposedCorrectionInput[] {
  const out: ProposedCorrectionInput[] = [];
  for (const field of CORRECTABLE_FIELDS) {
    const c = changes[field];
    if (c && c.changed && c.reason !== '') {
      const trimmed = c.proposedValue.trim();
      out.push({
        field,
        reason: c.reason,
        ...(trimmed !== '' ? { proposedValue: trimmed } : {}),
      });
    }
  }
  return out;
}

/** TRUE when the patient proposed at least one change (drives the submit path
 *  + the review-vs-clean copy). */
export function hasProposedChange(
  changes: Partial<Record<IntakeCorrectionField, FieldChangeState>>,
): boolean {
  return buildProposedCorrections(changes).length > 0;
}

// --- The presentation-side submit gates (founder corrective on T-1/T-4) ------

/** TRUE when the details section is COMPLETE as a presentation gate
 *  (AUG-12-WALK-BLOCKERS-V1 F5 -- ONE PRIMARY ACTION: the mid-form
 *  "All correct" second-submit is CUT; the single bottom submit owns
 *  completion). The read-back rows ARE the confirmation; the section is
 *  incomplete ONLY while an opened Fix editor holds an incomplete bounded
 *  correction (no proposed value or no reason). This gate is CLIENT-RENDER
 *  ONLY: the server payload, the intake contract, the identity law, and the
 *  correction semantics are untouched by it. */
export function detailsSectionComplete(
  changes: Partial<Record<IntakeCorrectionField, FieldChangeState>>,
): boolean {
  const open = Object.values(changes).filter(
    (c): c is FieldChangeState => c !== undefined && c.changed,
  );
  return open.every((c) => c.proposedValue.trim() !== '' && c.reason !== '');
}

export interface IntakeGateFacts {
  readonly detailsComplete: boolean;
  readonly confirmedName: string;
  readonly mobile: string;
  readonly gender: string;
  readonly missingQuestionKeys: readonly string[];
  /** FD-30 (UX LAW 1): a known number CONFIRMS instead of re-collecting --
   *  the last-4 chip satisfies the mobile section. */
  readonly phoneConfirmed?: boolean;
  /** True when the wire served a last-4 to confirm against. */
  readonly phoneConfirmAvailable?: boolean;
  /** F8 (AUG-12-WALK-BLOCKERS-V1, founder JIT ruling): a number ON FILE
   *  satisfies the mobile section outright -- the re-type-to-confirm ask is
   *  CUT; the patient sees their own number instead. */
  readonly phoneOnFileSatisfied?: boolean;
  /** FD-32: gender requiredness IDENTICAL to registration (org config);
   *  default true keeps the pre-package behavior for older wires. */
  readonly genderRequired?: boolean;
  /** F7 (AUG-12-WALK-BLOCKERS-V1): the merged reason section's SINGLE
   *  required signal -- true when the question set requires a reason, none
   *  is on file from the desk, and the patient gave neither chip nor text. */
  readonly complaintMissing?: boolean;
  /** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the org's registration
   *  required-field tokens (the wire's registrationRequiredFields). The
   *  form gates the config-gated extras (relation_name / occupation /
   *  handling_category) ONLY when the contract names them -- so the ONE
   *  Complete Registration action can actually satisfy the contract.
   *  Absent = seeded-default posture (no extras gated). */
  readonly registrationRequiredFields?: readonly string[];
  readonly relationName?: string;
  readonly occupation?: string;
  readonly handlingCategory?: string;
  /** REGISTRATION-CONTRACT-COMPLETE-PREP-V1: the 20260808_1 contract
   *  fields, gated ONLY when the tenant contract names them (same posture
   *  as the F3 extras above). */
  readonly nameTitle?: string;
  readonly lastName?: string;
  readonly relationTitle?: string;
  readonly stateField?: string;
  readonly pincode?: string;
  readonly addressDoor?: string;
  readonly addressStreet?: string;
  readonly addressArea?: string;
  readonly addressLandmark?: string;
  readonly addressTaluka?: string;
  readonly addressCountry?: string;
  /** REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the alternate-number owner
   *  detail + the emergency-contact trio, gated ONLY when the tenant contract
   *  names them (same posture as the extras above). */
  readonly altMobile?: string;
  readonly altMobileRelation?: string;
  readonly altMobileRelationName?: string;
  readonly emergencyContactName?: string;
  readonly emergencyContactRelation?: string;
  readonly emergencyContactPhone?: string;
  /** REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1 (D2/D3): the two required
   *  keys the form used to POST but never ASK. `city` had a state variable
   *  and a wire slot with NO control; `age_or_dob` is a LAW field the server
   *  gates and the form did not. Both are now first-class gate inputs, so
   *  the submit button and the server agree on completability. */
  readonly city?: string;
  /** TRUE when an age or a parsed DOB is known (the derived age view). */
  readonly ageKnown?: boolean;
}

/** Canonical key -> the DOM id the focus jump targets. ONE map, so a new
 *  required key cannot be gated without also being reachable. */
export const REGISTRATION_SECTION_IDS: Readonly<Record<string, string>> =
  Object.freeze({
    full_name: 'pim-sec-details',
    mobile: 'pim-sec-mobile',
    age_or_dob: 'pim-sec-dob',
    gender: 'pim-sec-gender',
    relation_name: 'pim-sec-relation',
    city: 'pim-sec-city',
    occupation: 'pim-sec-occupation',
    handling_category: 'pim-sec-handling',
    reason_for_visit: 'pim-sec-complaint',
    // REGISTRATION-CONTRACT-COMPLETE-PREP-V1: the 20260808_1 contract
    // fields. Required members always render VISIBLE (the plan promotes a
    // required expander resident out of the expander), so every focus
    // target here is reachable.
    name_title: 'pim-sec-name-title',
    last_name: 'pim-sec-last-name',
    relation_title: 'pim-sec-relation-title',
    state: 'pim-sec-state',
    pincode: 'pim-sec-pincode',
    address_door: 'pim-sec-address-door',
    address_street: 'pim-sec-address-street',
    address_area: 'pim-sec-address-area',
    address_landmark: 'pim-sec-address-landmark',
    address_taluka: 'pim-sec-address-taluka',
    address_country: 'pim-sec-address-country',
    // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the alt-number owner detail
    // + emergency trio. All render VISIBLE (never behind an expander), so
    // every focus target is reachable.
    alt_mobile: 'pim-sec-alt-mobile',
    alt_mobile_relation: 'pim-sec-alt-mobile-relation',
    alt_mobile_relation_name: 'pim-sec-alt-mobile-relation-name',
    emergency_contact_name: 'pim-sec-emergency-name',
    emergency_contact_relation: 'pim-sec-emergency-relation',
    emergency_contact_phone: 'pim-sec-emergency-phone',
  });

/**
 * THE ONE MISSING-FIELD DERIVATION, in CANONICAL KEYS
 * (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1, D2/D3).
 *
 * Everything downstream reads THIS: the sticky needs-line names these keys in
 * desk voice, the submit button counts them, and the focus jump maps them
 * through REGISTRATION_SECTION_IDS. The keys are the tenant config's own
 * tokens, so "the config requires it" and "the form blocks on it" are the
 * same sentence.
 *
 * An EMPTY `registrationRequiredFields` (an older wire / an older fixture)
 * gates nothing beyond the pre-existing name+phone+gender posture -- absence
 * of a contract is never read as "everything is required".
 */
export function missingRegistrationKeys(f: IntakeGateFacts): string[] {
  const required = new Set(f.registrationRequiredFields ?? []);
  const blank = (v: string | undefined): boolean => (v ?? '').trim() === '';
  const keys: string[] = [];

  // -- Identity ------------------------------------------------------------
  if (f.confirmedName.trim() === '') keys.push('full_name');
  // F8: a number on file satisfies outright; the last-4 confirm chip and a
  // typed NEW number stay lawful satisfactions.
  const phoneSatisfied =
    f.phoneOnFileSatisfied === true ||
    f.mobile.trim() !== '' ||
    (f.phoneConfirmAvailable === true && f.phoneConfirmed === true);
  if (!phoneSatisfied) keys.push('mobile');
  if (required.has('age_or_dob') && f.ageKnown !== true) keys.push('age_or_dob');
  // FD-32: requiredness parity with registration (the ONE config owner).
  if ((f.genderRequired ?? true) && f.gender.trim() === '') keys.push('gender');

  // -- Identity extras (20260808_1) ---------------------------------------
  if (required.has('name_title') && blank(f.nameTitle)) keys.push('name_title');
  if (required.has('last_name') && blank(f.lastName)) keys.push('last_name');

  // -- Contact: the alternate-number owner detail (PARTHA-CONTRACT Part C) --
  if (required.has('alt_mobile') && blank(f.altMobile)) keys.push('alt_mobile');
  if (required.has('alt_mobile_relation') && blank(f.altMobileRelation)) {
    keys.push('alt_mobile_relation');
  }
  if (
    required.has('alt_mobile_relation_name') &&
    blank(f.altMobileRelationName)
  ) {
    keys.push('alt_mobile_relation_name');
  }

  // -- Family / Address / Other -------------------------------------------
  if (required.has('relation_title') && blank(f.relationTitle)) {
    keys.push('relation_title');
  }
  if (required.has('relation_name') && blank(f.relationName)) {
    keys.push('relation_name');
  }
  if (required.has('city') && blank(f.city)) keys.push('city');
  if (required.has('address_area') && blank(f.addressArea)) {
    keys.push('address_area');
  }
  if (required.has('state') && blank(f.stateField)) keys.push('state');
  if (required.has('pincode') && blank(f.pincode)) keys.push('pincode');
  if (required.has('address_door') && blank(f.addressDoor)) {
    keys.push('address_door');
  }
  if (required.has('address_street') && blank(f.addressStreet)) {
    keys.push('address_street');
  }
  if (required.has('address_landmark') && blank(f.addressLandmark)) {
    keys.push('address_landmark');
  }
  if (required.has('address_taluka') && blank(f.addressTaluka)) {
    keys.push('address_taluka');
  }
  if (required.has('address_country') && blank(f.addressCountry)) {
    keys.push('address_country');
  }
  if (required.has('occupation') && blank(f.occupation)) keys.push('occupation');
  if (required.has('handling_category') && blank(f.handlingCategory)) {
    keys.push('handling_category');
  }

  // -- Emergency contact (PARTHA-CONTRACT Part D) -------------------------
  if (required.has('emergency_contact_name') && blank(f.emergencyContactName)) {
    keys.push('emergency_contact_name');
  }
  if (
    required.has('emergency_contact_relation') &&
    blank(f.emergencyContactRelation)
  ) {
    keys.push('emergency_contact_relation');
  }
  if (
    required.has('emergency_contact_phone') &&
    blank(f.emergencyContactPhone)
  ) {
    keys.push('emergency_contact_phone');
  }

  // -- Reason --------------------------------------------------------------
  if (f.complaintMissing === true) keys.push('reason_for_visit');

  return keys;
}

/** The unanswered required sections in VISUAL order (details -> mobile ->
 *  gender -> reason -> the questionnaire). The single submit stays DISABLED
 *  while this list is non-empty (F5: one primary action owns completion). */
export function missingIntakeSections(f: IntakeGateFacts): string[] {
  // ONE derivation (missingRegistrationKeys), mapped to focus targets. The
  // per-field logic lives THERE and only there -- this function is now the
  // key->DOM-id projection plus the two non-field gates below.
  const ids = missingRegistrationKeys(f).map(
    (key) => REGISTRATION_SECTION_IDS[key] ?? `pim-sec-${key}`,
  );
  // An OPEN Fix editor holding an incomplete bounded correction blocks the
  // details section even when the name itself reads fine (it is a
  // half-finished correction, not a missing field -- hence no canonical key).
  if (!f.detailsComplete && !ids.includes('pim-sec-details')) {
    ids.unshift('pim-sec-details');
  }
  for (const k of f.missingQuestionKeys) ids.push(`pim-sec-q-${k}`);
  return ids;
}

/** F5: the auto reason for an UNLINKED (pre-mint) inline edit -- the person
 *  has no golden record yet, so the edit is capture; the reason code still
 *  rides as provenance on the evidence trail. */
export function inlineEditReason(field: IntakeCorrectionField): IntakeCorrectionReason {
  if (field === 'dob') return 'dob_correction';
  if (field === 'phone') return 'new_phone_number';
  if (field === 'external_mr') return 'external_mr_missing';
  return 'name_correction';
}

// --- Submit outcome classification ------------------------------------------

export type PatientModeStage =
  | 'loading'
  | 'review'
  | 'submitting'
  | 'done'
  | 'review_required'
  | 'error';

/** Map a completion outcome to the terminal hand-back stage. A clean success
 *  (linked / created_and_linked / already_linked) -> 'done'; anything that
 *  needs staff attention -> 'review_required'. */
export function stageForOutcome(outcome: string): PatientModeStage {
  if (
    outcome === 'linked' ||
    outcome === 'created_and_linked' ||
    outcome === 'already_linked' ||
    // D3 (AUG-12-WALK-BLOCKERS-V1): a registered person's intake completes
    // as patient-reported reference -- a DONE handback, not a review stall.
    outcome === 'completed_reference'
  ) {
    return 'done';
  }
  return 'review_required';
}
